import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { todayStr, STATUS_LABELS, SOURCE_COLOR, STATUS_COLOR, formatDate, formatDateShort } from '../lib/api';
import { ReservationDialog, NewReservationDialog } from '../components/ReservationDialogs';
import { TableActionPopover } from './AdminFloorPlan';
import { undoableAction } from '../lib/undoable';
import SwipeableRow from '../components/SwipeableRow';
import DateStepper from '../components/DateStepper';
import WeatherWidget from '../components/WeatherWidget';
import { OccupancyForecast } from '../components/OccupancyForecast';
import { ChecklistCard } from '../components/ChecklistCard';
import { toast } from 'sonner';
import { Plus, UserPlus, Calendar, RefreshCw, Clock, Users, Map, Phone, Check, X as XIcon, Wine, Search, MessageSquare, Lock, CheckCircle2, Circle } from 'lucide-react';

const HOURS = Array.from({ length: 16 }, (_, i) => i + 8); // 8 to 23

const TABLE_STATE_COLOR = {
  free: '#22C55E',
  reserved: '#3B82F6',
  seated: '#F97316',
  blocked: '#EF4444',
  cleaning: '#9CA3AF',
};
const TABLE_STATE_LABEL = {
  free: 'Frei',
  reserved: 'Reserviert',
  seated: 'Sitzt',
  blocked: 'Gesperrt',
  cleaning: 'Reinigung',
};

function nowHM() {
  const d = new Date();
  return d.getHours() * 60 + d.getMinutes();
}

function getTableStateNow(table, reservations, combinations = [], dayBlocks = []) {
  if (table.status_override === 'blocked') return 'blocked';
  if (table.status_override === 'cleaning') return 'cleaning';
  if (!table.active) return 'blocked';
  const curBlk = nowHM();
  for (const b of dayBlocks) {
    if (b.table_id !== table.id) continue;
    const fullDay = b.full_day || !b.start_time || !b.end_time;
    if (fullDay) return 'blocked';
    const [bsh, bsm] = b.start_time.split(':').map(Number);
    const [beh, bem] = b.end_time.split(':').map(Number);
    if (bsh * 60 + bsm <= curBlk && curBlk < beh * 60 + bem) return 'blocked';
  }
  const cur = nowHM();
  const tableComboIds = new Set(
    (combinations || []).filter(c => (c.table_ids || []).includes(table.id)).map(c => c.id)
  );
  const tableResvs = reservations.filter(r =>
    r.assigned_table_id === table.id ||
    (r.assigned_combination_id && tableComboIds.has(r.assigned_combination_id))
  );
  // A physically seated/arrived guest occupies the table NOW even if the
  // planned end time has already passed. Completed/released reservations are
  // filtered out before this runs, so a remaining seated reservation means the
  // guest is still at the table.
  if (tableResvs.some(r => ['seated', 'arrived'].includes(r.status))) return 'seated';
  for (const r of tableResvs) {
    const [sh, sm] = r.start_time.split(':').map(Number);
    const [eh, em] = r.end_time.split(':').map(Number);
    const s = sh * 60 + sm;
    const e = eh * 60 + em;
    if (s <= cur && cur < e) {
      if (['confirmed', 'pending'].includes(r.status)) return 'reserved';
    }
  }
  // Has reservation later today?
  const upcoming = tableResvs.some(r => ['confirmed', 'pending'].includes(r.status));
  if (upcoming) return 'reserved';
  return 'free';
}

// Live 15-minute countdown for tables in 'cleaning' status (based on cleaning_started_at)
function cleaningSecondsLeft(startedAt, minutes = 15) {
  if (!startedAt) return minutes * 60;
  const end = new Date(startedAt).getTime() + minutes * 60 * 1000;
  return Math.max(0, Math.round((end - Date.now()) / 1000));
}

function CleaningTimer({ startedAt, minutes = 15 }) {
  const [left, setLeft] = useState(() => cleaningSecondsLeft(startedAt, minutes));
  useEffect(() => {
    setLeft(cleaningSecondsLeft(startedAt, minutes));
    const t = setInterval(() => setLeft(cleaningSecondsLeft(startedAt, minutes)), 1000);
    return () => clearInterval(t);
  }, [startedAt, minutes]);
  if (left <= 0) return <span className="text-[11px] font-mono font-semibold text-[#22C55E]">fertig</span>;
  const mm = Math.floor(left / 60);
  const ss = left % 60;
  return (
    <span className="text-[11px] font-mono font-semibold text-[#1C1C19]" data-testid="cleaning-timer">
      {mm}:{String(ss).padStart(2, '0')}
    </span>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [date, setDate] = useState(todayStr());
  const [reservations, setReservations] = useState([]);
  const [allReservations, setAllReservations] = useState([]);
  const [tables, setTables] = useState([]);
  const [areas, setAreas] = useState([]);
  const [tags, setTags] = useState([]);
  const [selected, setSelected] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [showWalkIn, setShowWalkIn] = useState(false);
  const [popoverTableId, setPopoverTableId] = useState(null);
  const [defaultTable, setDefaultTable] = useState(null);
  const [splitMode, setSplitMode] = useState(false);
  const [waitlist, setWaitlist] = useState([]);
  const [combinations, setCombinations] = useState([]);
  const [settings, setSettings] = useState({});
  const [dayBlocks, setDayBlocks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  const matchResv = (r, q) => {
    if (!q) return true;
    const s = q.trim().toLowerCase();
    return (r.customer_name || '').toLowerCase().includes(s)
      || (r.phone || '').toLowerCase().includes(s)
      || (r.email || '').toLowerCase().includes(s);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [resR, tR, aR, wR, tagR, cbR, dbR, stR] = await Promise.all([
        api.get(`/reservations?date=${date}`),
        api.get('/tables'),
        api.get('/areas'),
        api.get(`/waitlist?active_only=true`).catch(() => ({ data: [] })),
        api.get('/tags').catch(() => ({ data: [] })),
        api.get('/table-combinations/all').catch(() => ({ data: [] })),
        api.get(`/table-day-blocks?date=${date}`).catch(() => ({ data: [] })),
        api.get('/settings').catch(() => ({ data: {} })),
      ]);
      setReservations(resR.data.filter(r => r.status !== 'completed'));
      setAllReservations(resR.data);
      setTables(tR.data);
      setAreas(aR.data);
      setWaitlist(wR.data);
      setTags(tagR.data);
      setCombinations(cbR.data);
      setDayBlocks(dbR.data || []);
      setSettings(stR.data || {});
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh every 30s so new customer reservations appear without manual reload
  useEffect(() => {
    const t = setInterval(() => { load(); }, 30000);
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => { clearInterval(t); window.removeEventListener('focus', onFocus); };
  }, [load]);

  // ----- Swipe quick-actions for mobile rows -----
  // All actions show a toast with an "Rückgängig" button that reverts to the
  // previous status. Undo window: 8 seconds.
  const setStatusQuick = async (r, newStatus, successMsg) => {
    const prevStatus = r.status;
    try {
      await api.post(`/reservations/${r.id}/status`, { status: newStatus });
      toast.success(successMsg, {
        duration: 8000,
        action: {
          label: 'Rückgängig',
          onClick: async () => {
            try {
              await api.post(`/reservations/${r.id}/status`, { status: prevStatus });
              toast.message('Rückgängig gemacht');
              load();
            } catch (e) {
              toast.error(e.response?.data?.detail || 'Konnte nicht rückgängig machen');
            }
          },
        },
      });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Aktualisieren');
    }
  };

  // ----- Table-popover actions (same behaviour as the floor plan) -----
  const openNewForTable = (table, walkIn, share = false) => {
    setPopoverTableId(null);
    setDefaultTable(table);
    setSplitMode(!!share);
    if (walkIn) setShowWalkIn(true);
    else setShowNew(true);
  };

  const quickWalkIn = async (table) => {
    if (table.status_override === 'blocked') { toast.error('Tisch ist gesperrt'); return; }
    setPopoverTableId(null);
    try {
      const now = new Date();
      const start_time = `${String(now.getHours()).padStart(2, '0')}:${String(Math.floor(now.getMinutes() / 5) * 5).padStart(2, '0')}`;
      const r = await api.post('/reservations/quick-walkin', { table_id: table.id, date, start_time });
      const resv = r.data || {};
      toast.success(`Walk-in · Tisch ${table.table_number} · ${resv.persons || 2} Pers.${resv.end_time ? ` · bis ${resv.end_time}` : ''}`);
      load();
    } catch (e) {
      if (e.response?.status === 409) {
        toast.warning(e.response?.data?.detail || 'Tisch gerade nicht frei – bitte Details prüfen.');
        openNewForTable(table, true);
      } else {
        toast.error(e.response?.data?.detail || 'Walk-in fehlgeschlagen');
      }
    }
  };

  const releaseTable = async (resv) => {
    setPopoverTableId(null);
    const prevEnd = resv.end_time;
    const prevStatus = resv.status;
    try {
      await api.post(`/reservations/${resv.id}/release-table`);
      undoableAction({
        label: `Tisch freigegeben (${resv.customer_name})`,
        description: `Ende zurück auf ${prevEnd}?`,
        onUndo: () => api.put(`/reservations/${resv.id}`, { end_time: prevEnd, status: prevStatus }),
        onUndone: load,
      });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Freigeben');
    }
  };

  const seatGuest = async (resv) => {
    setPopoverTableId(null);
    const prevStatus = resv.status;
    try {
      await api.post(`/reservations/${resv.id}/status`, { status: 'seated' });
      undoableAction({
        label: `${resv.customer_name} sitzt`,
        description: `Zurück zu „${prevStatus}"?`,
        onUndo: () => api.post(`/reservations/${resv.id}/status`, { status: prevStatus }),
        onUndone: load,
      });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler');
    }
  };

  const reduceCombination = async (resvId, keepTableIds) => {
    const keep = Array.isArray(keepTableIds) ? keepTableIds : [keepTableIds];
    const keepNums = keep.map(id => tables.find(t => t.id === id)?.table_number).filter(Boolean).join(' + ');
    try {
      await api.put(`/reservations/${resvId}`, { table_ids: keep });
      toast.success(keep.length > 1
        ? `Kombination reduziert – Tische ${keepNums} behalten, übrige frei.`
        : `Kombination aufgelöst – nur Tisch ${keepNums} behalten, übrige frei.`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Tisch konnte nicht freigegeben werden (Konflikt).');
    }
  };

  // Reservations of the day relevant for a table (incl. combinations), without
  // cancelled/declined/no-show, sorted by start.
  const dayResvsForTable = (t) => {
    const comboIds = new Set((combinations || []).filter(c => (c.table_ids || []).includes(t.id)).map(c => c.id));
    return reservations
      .filter(r => !['cancelled', 'declined', 'no_show'].includes(r.status)
        && (r.assigned_table_id === t.id || (r.assigned_combination_id && comboIds.has(r.assigned_combination_id))))
      .sort((a, b) => a.start_time.localeCompare(b.start_time));
  };

  // Mark a single pre-ordered item as prepared (service/kitchen tick). Persistent
  // via backend so it survives the 30s reload and syncs across devices.
  const toggleOfferPrepared = async (resvId, idx, prepared) => {
    setAllReservations(prev => prev.map(r => {
      if (r.id !== resvId) return r;
      const offers = (r.selected_offers || []).map((o, i) => i === idx ? { ...o, prepared } : o);
      return { ...r, selected_offers: offers };
    }));
    try {
      await api.post(`/reservations/${resvId}/offer-prepared`, { index: idx, prepared });
    } catch (e) {
      toast.error('Konnte nicht gespeichert werden');
      load();
    }
  };

  // Tages-Statistik: aus ALLEN Reservierungen des Tages (inkl. abgeschlossener/
  // freigegebener) berechnet, damit die Kacheln im Lauf des Abends nicht
  // schrumpfen. Stornos/Absagen zählen nie; No-Shows zählen nicht als Gäste.
  const realResvs = allReservations.filter(r => !['cancelled', 'declined'].includes(r.status));
  const guestResvs = realResvs.filter(r => r.status !== 'no_show');
  const stats = {
    total: realResvs.length,
    confirmed: allReservations.filter(r => ['confirmed', 'arrived', 'seated', 'completed'].includes(r.status)).length,
    pending: allReservations.filter(r => r.status === 'pending').length,
    seated: allReservations.filter(r => ['seated', 'arrived'].includes(r.status)).length,
    persons: guestResvs.reduce((s, r) => s + (Number(r.persons) || 0), 0),
  };
  const offerResvs = guestResvs.filter(r =>
    Array.isArray(r.selected_offers) && r.selected_offers.length > 0
  );
  stats.offersCount = offerResvs.length;
  stats.offersValue = offerResvs.reduce((s, r) =>
    s + r.selected_offers.reduce((a, o) => a + (o.quantity || 1) * (Number(o.price) || 0), 0), 0);
  const offersValueStr = `${stats.offersValue.toFixed(2).replace('.', ',')} €`;

  // ----- Bestellungsübersicht (Live pre-order list for kitchen/service) -----
  // One row PER ordered item for the selected day. Entries are NEVER removed when a
  // reservation runs past its time (Überzeit) — the kitchen/service keeps every order
  // visible until it is manually checked off ("Fertig"). Cancelled/declined/no-show excluded.
  const tableLabelFor = (r) => {
    if (r.assigned_table_id) {
      const t = tables.find(x => x.id === r.assigned_table_id);
      return t ? t.table_number : '—';
    }
    if (r.assigned_combination_id) {
      const c = combinations.find(x => x.id === r.assigned_combination_id);
      if (c) return (c.table_ids || []).map(tid => tables.find(t => t.id === tid)?.table_number).filter(Boolean).join('+') || '—';
    }
    return '—';
  };
  const isTodaySel = date === todayStr();
  const preOrderRows = (() => {
    const src = allReservations.filter(r =>
      !['cancelled', 'declined', 'no_show'].includes(r.status) &&
      Array.isArray(r.selected_offers) && r.selected_offers.length > 0
    );
    const sorted = [...src].sort((a, b) => a.start_time.localeCompare(b.start_time));
    const rows = [];
    for (const r of sorted) {
      const tableLabel = tableLabelFor(r);
      r.selected_offers.forEach((o, idx) => {
        rows.push({
          key: `${r.id}-${idx}`,
          resvId: r.id,
          idx,
          prepared: !!o.prepared,
          time: r.start_time,
          table: tableLabel,
          name: r.customer_name,
          persons: r.persons,
          qty: o.quantity || 1,
          item: o.name,
          resv: r,
        });
      });
    }
    return rows;
  })();

  return (
    <div className="p-4 md:p-8 max-w-7xl" data-testid="admin-dashboard-page">
      <header className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6">
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-[0.2em] text-[#5C5C59]">Tagesübersicht</p>
          <h1 className="font-display text-2xl md:text-4xl text-[#1C1C19] truncate" data-testid="dashboard-date-label">{formatDate(date)}</h1>
        </div>
        <div className="flex flex-col md:flex-row md:items-center gap-2">
          <div className="flex items-center gap-2">
            <div className="flex-1 md:flex-none"><DateStepper value={date} onChange={setDate} testIdPrefix="dashboard-date" /></div>
            <button onClick={load} data-testid="refresh-btn" className="px-3 py-2 border border-[#E5E5E0] rounded text-sm hover:border-[#1C1C19] flex items-center gap-1 flex-shrink-0">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
          <div className="grid grid-cols-2 gap-2 md:flex">
            <button onClick={() => { setDefaultTable(null); setSplitMode(false); setShowWalkIn(true); }} data-testid="add-walkin-btn" className="px-4 py-2 border border-[#22C55E] text-[#22C55E] hover:bg-[#22C55E] hover:text-white rounded text-sm font-medium flex items-center justify-center gap-2 transition">
              <UserPlus className="w-4 h-4" /> Walk-in
            </button>
            <button onClick={() => { setDefaultTable(null); setShowNew(true); }} data-testid="add-reservation-btn" className="bg-[#1C1C19] hover:bg-[#3A3A35] text-white px-4 py-2 rounded text-sm font-medium flex items-center justify-center gap-2 transition">
              <Plus className="w-4 h-4" /> Reservierung
            </button>
          </div>
        </div>
      </header>

      <WeatherWidget />

      {/* Stats */}
      <div className="grid grid-cols-3 md:grid-cols-3 lg:grid-cols-6 gap-2 mb-6">
        <StatCard label="Gesamt" value={stats.total} testId="stat-total" />
        <StatCard label="Wartet" value={stats.pending} color="#EAB308" testId="stat-pending" />
        <StatCard label="Bestätigt" value={stats.confirmed} color="#3B82F6" testId="stat-confirmed" />
        <StatCard label="Sitzt" value={stats.seated} color="#F97316" testId="stat-seated" />
        <StatCard label="Gäste" value={stats.persons} icon={Users} testId="stat-persons" />
        <StatCard label="Vorbestellungen" value={stats.offersCount} sub={stats.offersValue > 0 ? offersValueStr : null} color="#B45309" icon={Wine} testId="stat-offers" />
      </div>

      {/* Bestellungsübersicht: Live-Liste aller Vorbestellungen (ein Eintrag pro Artikel) */}
      <div className="bg-white border border-[#E5E5E0] rounded-lg overflow-hidden mb-6" data-testid="preorder-overview">
        <div className="px-4 md:px-6 py-3 border-b border-[#E5E5E0] flex items-center gap-2">
          <Wine className="w-4 h-4 text-[#B45309]" />
          <h2 className="font-display text-lg text-[#1C1C19]">Bestellungsübersicht</h2>
          <span className="text-xs text-[#5C5C59]" data-testid="preorder-count">· {preOrderRows.length}</span>
        </div>
        {preOrderRows.length === 0 ? (
          <div className="p-8 text-center text-[#5C5C59]" data-testid="preorder-empty">
            {isTodaySel ? 'Keine Vorbestellungen für heute.' : 'Keine Vorbestellungen für diesen Tag.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[620px]" data-testid="preorder-table">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-[#5C5C59] border-b border-[#E5E5E0]">
                  <th className="px-3 py-2 font-medium text-center">Fertig</th>
                  <th className="px-4 py-2 font-medium">Uhrzeit</th>
                  <th className="px-4 py-2 font-medium">Tisch</th>
                  <th className="px-4 py-2 font-medium">Name</th>
                  <th className="px-4 py-2 font-medium text-center">Personen</th>
                  <th className="px-4 py-2 font-medium text-center">Menge</th>
                  <th className="px-4 py-2 font-medium">Artikel</th>
                </tr>
              </thead>
              <tbody>
                {preOrderRows.map(row => (
                  <tr
                    key={row.key}
                    onClick={() => setSelected(row.resv)}
                    data-testid={`preorder-row-${row.key}`}
                    className={`border-b border-[#F0F0EC] last:border-0 cursor-pointer transition ${row.prepared ? 'bg-[#F0FAF2] hover:bg-[#E6F6EA]' : 'hover:bg-[#FBF6EE]'}`}
                  >
                    <td className="px-3 py-2.5 text-center">
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleOfferPrepared(row.resvId, row.idx, !row.prepared); }}
                        data-testid={`preorder-prepared-${row.key}`}
                        title={row.prepared ? 'Als nicht vorbereitet markieren' : 'Als vorbereitet markieren'}
                        aria-pressed={row.prepared}
                        className="inline-flex items-center justify-center p-1 rounded-full hover:scale-110 transition"
                      >
                        {row.prepared
                          ? <CheckCircle2 className="w-5 h-5 text-[#22C55E]" />
                          : <Circle className="w-5 h-5 text-[#C9C9C2]" />}
                      </button>
                    </td>
                    <td className={`px-4 py-2.5 font-mono font-semibold whitespace-nowrap ${row.prepared ? 'text-[#9CA3AF]' : 'text-[#1C1C19]'}`}>{row.time}</td>
                    <td className={`px-4 py-2.5 font-mono whitespace-nowrap ${row.prepared ? 'text-[#9CA3AF]' : 'text-[#1C1C19]'}`}>{row.table}</td>
                    <td className={`px-4 py-2.5 whitespace-nowrap ${row.prepared ? 'text-[#9CA3AF]' : 'text-[#1C1C19]'}`}>{row.name}</td>
                    <td className="px-4 py-2.5 text-center text-[#5C5C59]">{row.persons}</td>
                    <td className={`px-4 py-2.5 text-center font-semibold whitespace-nowrap ${row.prepared ? 'text-[#9CA3AF]' : 'text-[#B45309]'}`}>{row.qty}×</td>
                    <td className={`px-4 py-2.5 ${row.prepared ? 'text-[#9CA3AF] line-through' : 'text-[#1C1C19]'}`}>{row.item}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Auslastungs-Prognose: Plätze belegt pro Zeitfenster + Gäste-Tabelle je Uhrzeit-Slot.
          Nutzt ALLE Reservierungen inkl. abgeschlossener/freigegebener, damit die
          Tages-Auslastung im Lauf des Abends nicht schrumpft. */}
      <OccupancyForecast
        reservations={allReservations}
        tables={tables}
        slotMinutes={Number(settings.occupancy_slot_minutes) || 15}
        onSlotMinutesChange={async (val) => {
          setSettings(s => ({ ...s, occupancy_slot_minutes: val }));
          try {
            await api.put('/settings', { occupancy_slot_minutes: val });
          } catch (e) {
            toast.error('Slot-Abstand konnte nicht gespeichert werden');
          }
        }}
      />

      {/* Waitlist widget — all open requests across all days */}
      {waitlist.filter(w => !['converted', 'declined', 'expired'].includes(w.status)).length > 0 && (
        <button
          onClick={() => navigate('/admin/waitlist')}
          data-testid="waitlist-widget"
          className="w-full bg-[#FFFBEB] border border-[#EAB308] rounded-lg p-4 mb-6 flex items-center gap-3 hover:bg-[#FEF3C7] transition text-left"
        >
          <div className="w-10 h-10 rounded-full bg-[#EAB308]/15 flex items-center justify-center flex-shrink-0">
            <Clock className="w-5 h-5 text-[#EAB308]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display text-base text-[#1C1C19]">
              Warteliste: {waitlist.filter(w => !['converted', 'declined', 'expired'].includes(w.status)).length} offene {waitlist.filter(w => !['converted', 'declined', 'expired'].includes(w.status)).length === 1 ? 'Anfrage' : 'Anfragen'}
            </div>
            <div className="text-xs text-[#5C5C59] mt-0.5 truncate">
              {waitlist.slice(0, 3).map(w => `${formatDateShort(w.date)} ${w.preferred_time} · ${w.customer_name} (${w.persons}P)`).join(' · ')}
              {waitlist.length > 3 && ` · +${waitlist.length - 3} weitere`}
            </div>
          </div>
          <span className="text-xs text-[#1C1C19] flex items-center gap-1">Öffnen →</span>
        </button>
      )}

      {/* Tables overview */}
      {tables.length > 0 && (
        <div className="bg-white border border-[#E5E5E0] rounded-lg overflow-hidden mb-6" data-testid="dashboard-tables-overview">
          <div className="px-4 md:px-6 py-3 border-b border-[#E5E5E0] flex items-center justify-between">
            <h2 className="font-display text-lg text-[#1C1C19]">Tische jetzt</h2>
            <button onClick={() => navigate('/admin/floor-plan')} data-testid="dashboard-goto-floorplan" className="text-xs text-[#5C5C59] hover:text-[#1C1C19] flex items-center gap-1">
              <Map className="w-3 h-3" /> Tischplan öffnen
            </button>
          </div>
          <div className="p-4 space-y-3">
            {areas.filter(a => tables.some(t => t.area_id === a.id)).map(area => {
              const at = tables.filter(t => t.area_id === area.id);
              return (
                <div key={area.id} data-testid={`dashboard-area-${area.id}`}>
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="w-2 h-2 rounded-full" style={{ background: area.color }}></span>
                    <span className="text-xs uppercase tracking-wider text-[#5C5C59] font-medium">{area.name}</span>
                  </div>
                  <div className="grid grid-cols-6 lg:grid-cols-[repeat(15,minmax(0,1fr))] gap-1 lg:gap-1" data-testid={`dashboard-area-grid-${area.id}`}>
                    {at.map(t => {
                      const state = getTableStateNow(t, reservations, combinations, dayBlocks);
                      const tableComboIds = new Set(
                        (combinations || []).filter(c => (c.table_ids || []).includes(t.id)).map(c => c.id)
                      );
                      const cur = nowHM();
                      const inWindow = (r) => {
                        const [sh, sm] = r.start_time.split(':').map(Number);
                        const [eh, em] = r.end_time.split(':').map(Number);
                        return sh * 60 + sm <= cur && cur < eh * 60 + em;
                      };
                      const tResvs = reservations.filter(r =>
                        r.assigned_table_id === t.id || (r.assigned_combination_id && tableComboIds.has(r.assigned_combination_id))
                      );
                      const seatedNow = tResvs.find(r => ['seated', 'arrived'].includes(r.status) && inWindow(r))
                        || tResvs.find(r => ['seated', 'arrived'].includes(r.status));
                      const reservedNow = tResvs.find(r => ['confirmed', 'pending'].includes(r.status) && inWindow(r));
                      const nextResv = tResvs
                        .filter(r => ['confirmed', 'pending'].includes(r.status))
                        .sort((a, b) => a.start_time.localeCompare(b.start_time))[0];
                      const reservedResv = reservedNow || nextResv;
                      const isCleaning = state === 'cleaning';
                      const titleDetail = state === 'seated' && seatedNow ? ` · ${seatedNow.customer_name}`
                        : state === 'reserved' && reservedResv ? ` · ab ${reservedResv.start_time}` : '';
                      return (
                        <button
                          key={t.id}
                          onClick={() => setPopoverTableId(t.id)}
                          data-testid={`dashboard-table-${t.id}`}
                          title={`Tisch ${t.table_number} – ${TABLE_STATE_LABEL[state]}${titleDetail} · Klick = Tisch-Dialog öffnen`}
                          className="flex flex-col items-center lg:items-start gap-0.5 px-1 py-1 lg:px-1.5 lg:py-1 rounded border-2 text-left hover:scale-[1.03] transition lg:min-h-[44px] overflow-hidden"
                          style={{
                            borderColor: TABLE_STATE_COLOR[state],
                            background: isCleaning ? '#FFFFFF' : TABLE_STATE_COLOR[state] + '14',
                            color: '#1C1C19',
                          }}
                        >
                          <div className="flex items-center gap-1 lg:gap-1.5 w-full justify-center lg:justify-start">
                            <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: TABLE_STATE_COLOR[state] }}></span>
                            <span className="font-mono text-xs lg:text-sm font-semibold">{t.table_number}</span>
                          </div>
                          {/* Details nur auf Desktop – auf Mobile nur Nummer + Punkt */}
                          <div className="hidden lg:block w-full">
                            {state === 'free' && (
                              <span className="text-[10px] text-[#5C5C59]" data-testid={`dashboard-table-detail-${t.id}`}>{t.min_persons}-{t.max_persons} Pers.</span>
                            )}
                            {state === 'reserved' && (
                              <span className="text-[11px] font-semibold truncate block w-full" style={{ color: TABLE_STATE_COLOR.reserved }} data-testid={`dashboard-table-detail-${t.id}`}>
                                {reservedResv ? `ab ${reservedResv.start_time}` : 'Reserviert'}
                              </span>
                            )}
                            {state === 'seated' && (
                              <span className="text-[11px] font-semibold truncate block w-full" style={{ color: TABLE_STATE_COLOR.seated }} data-testid={`dashboard-table-detail-${t.id}`}>
                                {seatedNow ? seatedNow.customer_name : 'Sitzt'}
                              </span>
                            )}
                            {state === 'blocked' && (
                              <span className="text-[11px] font-semibold" style={{ color: TABLE_STATE_COLOR.blocked }} data-testid={`dashboard-table-detail-${t.id}`}>Gesperrt</span>
                            )}
                            {state === 'cleaning' && (
                              <span className="flex items-center gap-1 w-full" data-testid={`dashboard-table-detail-${t.id}`}>
                                <span className="text-[9px] uppercase text-[#5C5C59]">Reinigung</span>
                                <CleaningTimer startedAt={t.cleaning_started_at} />
                              </span>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
          {/* Mini legend */}
          <div className="px-4 md:px-6 py-2 border-t border-[#E5E5E0] flex flex-wrap gap-3 text-[10px]">
            {Object.entries(TABLE_STATE_LABEL).map(([k, v]) => (
              <div key={k} className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full" style={{ background: TABLE_STATE_COLOR[k] }}></span>
                <span className="text-[#5C5C59]">{v}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Mitarbeiter-Checkliste für den Tag */}
      <ChecklistCard date={date} title="Checkliste für den Tag" compact />

      {/* Reservations list */}
      <div className="bg-white border border-[#E5E5E0] rounded-lg overflow-hidden">
        <div className="px-4 md:px-6 py-3 border-b border-[#E5E5E0] flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <h2 className="font-display text-lg text-[#1C1C19]">Reservierungen</h2>
          <div className="flex items-center gap-3">
            <div className="relative flex-1 sm:flex-none">
              <Search className="w-4 h-4 absolute left-2 top-2.5 text-[#5C5C59]" />
              <input
                type="text"
                placeholder="Gast suchen (Name, Tel., E-Mail)..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                data-testid="dashboard-search-input"
                className="w-full sm:w-64 pl-8 pr-7 py-1.5 border border-[#E5E5E0] rounded text-sm focus:outline-none focus:border-[#1C1C19]"
              />
              {search && (
                <button onClick={() => setSearch('')} data-testid="dashboard-search-clear" className="absolute right-2 top-2 text-[#5C5C59] hover:text-[#1C1C19]">
                  <XIcon className="w-4 h-4" />
                </button>
              )}
            </div>
            <span className="text-xs text-[#5C5C59] whitespace-nowrap" data-testid="dashboard-count">
              {search ? `${reservations.filter(r => matchResv(r, search)).length} / ${reservations.length}` : reservations.length} Einträge
            </span>
          </div>
        </div>
        {(() => { const filtered = [...reservations].filter(r => matchResv(r, search)); return (
        filtered.length === 0 ? (
          <div className="p-12 text-center text-[#5C5C59]" data-testid="dashboard-empty">
            <Calendar className="w-10 h-10 mx-auto mb-3 opacity-40" />
            <p>{search ? 'Keine Treffer für die Suche.' : 'Keine Reservierungen für diesen Tag.'}</p>
          </div>
        ) : (
          <div className="divide-y divide-transparent">
            {filtered.sort((a, b) => a.start_time.localeCompare(b.start_time)).map(r => {
              const table = tables.find(t => t.id === r.assigned_table_id);
              const combo = r.assigned_combination_id ? combinations.find(c => c.id === r.assigned_combination_id) : null;
              const comboTableNums = combo ? (combo.table_ids || []).map(tid => tables.find(t => t.id === tid)?.table_number).filter(Boolean).join('+') : '';
              const area = areas.find(a => a.id === r.area_id);
              const isVip = (r.tag_ids || []).some(tid => tags.find(t => t.id === tid)?.is_vip);
              const tischDisplay = combo ? (comboTableNums || combo.name) : (table ? table.table_number : '');
              const offers = Array.isArray(r.selected_offers) ? r.selected_offers : [];
              const offersSummary = offers.map(o => `${o.quantity}× ${o.name}`).join(', ');
              // Swipe actions: only for active statuses on mobile.
              const isActiveStatus = ['confirmed', 'pending', 'arrived', 'seated'].includes(r.status);
              // Skip the "arrived" intermediate step on swipe — go straight to "seated"
              // because in service workflow the guest is sitting when staff marks them.
              const leftActions = isActiveStatus && r.status !== 'seated' ? [{
                icon: Check, label: 'Sitzt', color: '#22C55E',
                onAction: () => setStatusQuick(r, 'seated', 'Gast sitzt'),
                testId: `swipe-seated-${r.id}`,
              }] : [];
              // Swipe left → release table (mark completed). Available once
              // the guest is sitting / arrived / about to leave.
              const rightActions = isActiveStatus ? [{
                icon: XIcon, label: 'Tisch frei', color: '#F97316',
                onAction: () => setStatusQuick(r, 'completed', 'Tisch freigegeben'),
                testId: `swipe-release-${r.id}`,
              }] : [];
              return (
                <div key={r.id}>
                  {/* MOBILE swipe-enabled row */}
                  <SwipeableRow
                    leftActions={leftActions}
                    rightActions={rightActions}
                    rowClassName="md:hidden border-b border-[#E5E5E0]"
                    dataTestId={`swipe-row-${r.id}`}
                  >
                    <button
                      onClick={() => setSelected(r)}
                      data-testid={`reservation-row-${r.id}`}
                      className="w-full text-left hover:bg-[#F9F9F7] transition"
                    >
                      <div className="flex items-center gap-2 px-3 py-2">
                        <span className="w-1 self-stretch rounded-full shrink-0" style={{ background: STATUS_COLOR[r.status] }}></span>
                        <div className="font-mono leading-tight w-12 shrink-0">
                          <div className="text-sm text-[#1C1C19]">{r.start_time}</div>
                          <div className="text-[10px] text-[#5C5C59]">{r.end_time}</div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="font-medium text-sm text-[#1C1C19] truncate">{r.customer_name}</span>
                            {isVip && <span className="text-[9px] px-1 py-0 rounded font-mono uppercase" style={{ background: '#A855F720', color: '#A855F7' }}>VIP</span>}
                          </div>
                          <div className="flex items-center gap-1.5 text-[11px] text-[#5C5C59] mt-0.5">
                            <span className="inline-flex items-center gap-0.5"><Users className="w-3 h-3" />{r.persons}</span>
                            {tischDisplay && (
                              <span
                                role="button"
                                tabIndex={0}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const tid = combo ? (combo.table_ids || [])[0] : table?.id;
                                  if (tid) navigate(`/admin/floor-plan?date=${date}&highlight=${tid}`);
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.stopPropagation();
                                    const tid = combo ? (combo.table_ids || [])[0] : table?.id;
                                    if (tid) navigate(`/admin/floor-plan?date=${date}&highlight=${tid}`);
                                  }
                                }}
                                title="Im Tischplan anzeigen"
                                data-testid={`dash-tisch-link-${r.id}`}
                                className="inline-flex items-center gap-0.5 hover:text-[#1C1C19] active:scale-95 underline-offset-2 hover:underline cursor-pointer"
                              >
                                {combo && <span className="text-[8px] px-1 rounded font-mono uppercase bg-[#3B82F6]/15 text-[#3B82F6]">Gr</span>}
                                <span>T{tischDisplay}</span>
                              </span>
                            )}
                            {area && <span className="truncate" style={{ color: area.color }}>· {area.name}</span>}
                          </div>
                          {offers.length > 0 && (
                            <div className="flex items-center gap-1 text-[11px] text-[#B45309] mt-0.5" data-testid={`dash-res-offers-${r.id}`} title={offersSummary}>
                              <Wine className="w-3 h-3 shrink-0" />
                              <span className="truncate">{offersSummary}</span>
                            </div>
                          )}
                          {r.note && (
                            <div className="flex items-center gap-1 text-[11px] text-[#5C5C59] mt-0.5" title={r.note} data-testid={`dash-res-note-${r.id}`}>
                              <MessageSquare className="w-3 h-3 shrink-0" /><span className="truncate">{r.note}</span>
                            </div>
                          )}
                          {r.internal_note && (
                            <div className="flex items-center gap-1 text-[11px] text-[#B45309] mt-0.5" title={r.internal_note} data-testid={`dash-res-internal-note-${r.id}`}>
                              <Lock className="w-3 h-3 shrink-0" /><span className="truncate">{r.internal_note}</span>
                            </div>
                          )}
                        </div>
                        {r.phone && (
                          <a
                            href={`tel:${r.phone}`}
                            onClick={e => e.stopPropagation()}
                            className="shrink-0 w-7 h-7 flex items-center justify-center rounded-full bg-[#F9F9F7] active:bg-[#E5E5E0]"
                            title={`Anrufen: ${r.phone}`}
                            data-testid={`dash-call-${r.id}`}
                          >
                            <Phone className="w-3 h-3 text-[#5C5C59]" />
                          </a>
                        )}
                      </div>
                    </button>
                  </SwipeableRow>

                  {/* DESKTOP / TABLET row (original) */}
                  <button
                    onClick={() => setSelected(r)}
                    data-testid={`reservation-row-desktop-${r.id}`}
                    className="hidden md:block w-full text-left hover:bg-[#F9F9F7] transition border-b border-[#E5E5E0]"
                  >
                    <div className="flex px-4 md:px-6 py-4 items-center gap-3">
                    <div className="flex items-center gap-3 md:w-32">
                      <span className="w-1 h-10 rounded" style={{ background: SOURCE_COLOR[r.source] }}></span>
                      <div>
                        <div className="font-mono text-base text-[#1C1C19]">{r.start_time}</div>
                        <div className="text-xs text-[#5C5C59] font-mono">– {r.end_time}</div>
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-[#1C1C19] truncate flex items-center gap-1.5">
                        {r.customer_name}
                        {isVip && (
                          <span className="text-[10px] px-1 py-0.5 rounded font-mono uppercase" style={{ background: '#A855F720', color: '#A855F7' }}>VIP</span>
                        )}
                      </div>
                      <div className="text-xs text-[#5C5C59]">{r.phone} {r.email && `· ${r.email}`}</div>
                      {(r.tag_ids || []).length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1" data-testid={`dash-res-tags-${r.id}`}>
                          {(r.tag_ids || []).map(tid => {
                            const t = tags.find(x => x.id === tid);
                            if (!t) return null;
                            return (
                              <span
                                key={tid}
                                title={t.name}
                                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px]"
                                style={{ background: (t.color || '#5C5C59') + '20', color: t.color || '#5C5C59' }}
                              >
                                {t.icon && <span className="text-xs leading-none">{t.icon}</span>}
                                <span>{t.name}</span>
                              </span>
                            );
                          })}
                        </div>
                      )}
                      {offers.length > 0 && (
                        <div className="flex items-center gap-1 mt-1 text-[11px] text-[#B45309]" data-testid={`dash-res-offers-desktop-${r.id}`}>
                          <Wine className="w-3.5 h-3.5 shrink-0" />
                          <span className="truncate">{offersSummary}</span>
                        </div>
                      )}
                      {r.note && (
                        <div className="flex items-center gap-1 mt-1 text-xs text-[#5C5C59]" title={r.note} data-testid={`dash-res-note-desktop-${r.id}`}>
                          <MessageSquare className="w-3.5 h-3.5 shrink-0" /><span className="truncate max-w-[280px]">{r.note}</span>
                        </div>
                      )}
                      {r.internal_note && (
                        <div className="flex items-center gap-1 mt-1 text-xs text-[#B45309]" title={r.internal_note} data-testid={`dash-res-internal-note-desktop-${r.id}`}>
                          <Lock className="w-3.5 h-3.5 shrink-0" /><span className="truncate max-w-[280px]">{r.internal_note}</span>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-sm">
                      <div className="text-[#5C5C59] flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {r.persons}</div>
                      {table && <div className="text-[#5C5C59]">Tisch {table.table_number}</div>}
                      {combo && (
                        <div className="text-[#5C5C59] flex items-center gap-1" title={`Tischgruppe „${combo.name}"`}>
                          <span className="text-[10px] px-1.5 py-0.5 rounded font-mono uppercase bg-[#3B82F6]/15 text-[#3B82F6]">Gruppe</span>
                          <span>{comboTableNums || combo.name}</span>
                        </div>
                      )}
                      {area && <div className="text-xs px-2 py-0.5 rounded border" style={{ borderColor: area.color, color: area.color }}>{area.name}</div>}
                      <span className="text-xs uppercase tracking-wider px-2 py-1 rounded font-mono" style={{ background: STATUS_COLOR[r.status] + '22', color: STATUS_COLOR[r.status] }}>
                        {STATUS_LABELS[r.status]}
                      </span>
                    </div>
                    </div>
                  </button>
                </div>
              );
            })}
          </div>
        )
        ); })()}
      </div>

      <ReservationDialog
        open={!!selected}
        onClose={() => setSelected(null)}
        reservation={selected}
        tables={tables}
        areas={areas}
        onUpdate={load}
      />
      <NewReservationDialog
        open={showNew}
        onClose={() => { setShowNew(false); setDefaultTable(null); }}
        tables={tables}
        areas={areas}
        defaultDate={date}
        defaultTableId={defaultTable?.id || ''}
        onCreated={load}
      />
      <NewReservationDialog
        open={showWalkIn}
        onClose={() => { setShowWalkIn(false); setDefaultTable(null); setSplitMode(false); }}
        tables={tables}
        areas={areas}
        defaultDate={date}
        defaultTableId={defaultTable?.id || ''}
        walkIn
        shareTable={splitMode}
        onCreated={load}
      />

      {popoverTableId && (() => {
        const t = tables.find(x => x.id === popoverTableId);
        if (!t) return null;
        const dResvs = dayResvsForTable(t);
        const state = getTableStateNow(t, reservations, combinations, dayBlocks);
        const tBlocks = dayBlocks.filter(b => b.table_id === t.id);
        return (
          <TableActionPopover
            table={t}
            state={state}
            dayResvs={dResvs}
            dayBlocks={tBlocks}
            tables={tables}
            combinations={combinations}
            onReduceCombination={reduceCombination}
            onClose={() => setPopoverTableId(null)}
            onSelectResv={(r) => { setSelected(r); setPopoverTableId(null); }}
            onReleaseTable={releaseTable}
            onSeatGuest={seatGuest}
            onAddWalkIn={() => openNewForTable(t, true)}
            onQuickWalkIn={() => quickWalkIn(t)}
            onSplitWalkIn={() => openNewForTable(t, true, true)}
            onAddReservation={() => openNewForTable(t, false)}
          />
        );
      })()}
    </div>
  );
}

function StatCard({ label, value, color, icon: Icon, testId, sub }) {
  return (
    <div className="bg-white border border-[#E5E5E0] rounded px-2 py-1.5 md:px-3 md:py-2" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider text-[#5C5C59] leading-tight">{label}</div>
      <div className="flex items-center gap-1.5">
        <div className="font-display text-lg md:text-xl leading-tight text-[#1C1C19]" style={color ? { color } : {}}>{value}</div>
        {Icon && <Icon className="w-3.5 h-3.5 text-[#5C5C59] flex-shrink-0" />}
        {sub && <div className="text-[10px] font-medium" style={color ? { color } : { color: '#5C5C59' }} data-testid={`${testId}-sub`}>{sub}</div>}
      </div>
    </div>
  );
}
